import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, IMAGES } from "@/lib/api";
import { Gift as GiftIcon, Loader2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { getRef } from "@/lib/ref";
import { GiftSteps, GiftStatus } from "@/components/GiftSteps";
import { GiftNameDialog } from "@/components/GiftNameDialog";

export default function Gift() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState("");
  const [markItem, setMarkItem] = useState(null);
  const [buyTarget, setBuyTarget] = useState(null);

  const load = useCallback(() => api.get(`/gift/${token}`)
    .then(({ data }) => setData(data))
    .catch(() => setData(false))
    .finally(() => setLoading(false)), [token]);

  useEffect(() => { load(); }, [load]);

  const errText = (err, fb) => {
    const d = err.response?.data?.detail;
    return Array.isArray(d) ? "Scrivi il tuo nome (almeno 2 lettere)" : d || fb;
  };

  const confirmMark = async ({ by, message }) => {
    try {
      await api.post(`/gift/${token}/mark`, { product_id: markItem.product_id, by, message });
      toast.success("Grazie! Il capo è segnato come regalato 🤍");
      setMarkItem(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Impossibile segnare il regalo"));
    }
  };

  const regala = async ({ by, message }) => {
    const productId = buyTarget?.product_id || null;
    const key = productId || "all";
    setBuying(key);
    try {
      const { data: res } = await api.post(`/gift/${token}/checkout`, { product_id: productId, by, message, ref: getRef() });
      if (res.skipped?.length) toast.info(`Non disponibili, esclusi: ${res.skipped.join(", ")}`);
      if (res.substituted?.length) toast.info(`Variante non disponibile, sostituita: ${res.substituted.join("; ")}`, { duration: 8000 });
      toast.success("Checkout pronto · sconto 15% applicato");
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(errText(err, "Impossibile preparare il regalo"));
    } finally {
      setBuying("");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
      <Loader2 className="animate-spin text-[#C87D75]" size={28} /></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] text-[#8C7F78]">
      Lista non trovata.</div>;
  }

  const sexLabel = data.sex === "maschio" ? "del piccolo" : data.sex === "femmina" ? "della piccola" : "del bebè";
  const accent = data.sex === "maschio" ? "#4A7496" : data.sex === "femmina" ? "#B5655C" : "#4E7363";

  const toGift = data.wishlist.filter((it) => !it.fully_gifted);
  const gifted = data.wishlist.filter((it) => it.fully_gifted);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="gift-page">
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={IMAGES.nursery} alt="" className="w-full h-full object-cover object-[center_40%]" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4">
          <div className="text-xs uppercase tracking-[0.25em]">Lista Nascita · Minime Baby</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3" data-testid="gift-title">
            {data.baby_name ? `La lista di ${data.baby_name}` : `La lista ${sexLabel} di ${data.name}`}
          </h1>
          {data.baby_name && <div className="mt-1 text-sm opacity-90" data-testid="gift-subtitle">preparata da {data.name}</div>}
          <p className="mt-2 opacity-90">Un pensiero di cotone per celebrare {data.baby_name ? `l'arrivo di ${data.baby_name}` : "il suo arrivo"} 🤍</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
        <div className="mb-8"><GiftSteps /></div>
        {toGift.length > 0 && <div className="rounded-2xl p-6 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ background: "#FDF6E7", border: "1px solid #F2D293" }}>
          <div className="text-center sm:text-left">
            <div className="font-display font-medium text-[#2D2825]">Regala con un clic</div>
            <div className="text-sm text-[#7a5f1f]">Lo sconto del 15% viene applicato in automatico al checkout con un codice personale per il tuo regalo.</div>
          </div>
          {toGift.length > 0 && (
            <button data-testid="gift-buy-all" onClick={() => setBuyTarget({ all: true })} disabled={!!buying}
              className="inline-flex items-center gap-2 rounded-full text-white px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: accent }}>
              {buying === "all" ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />} Regala tutto quello che manca
            </button>
          )}
        </div>}

        {data.wishlist.length === 0 ? (
          <div className="text-center text-[#8C7F78] py-10">La lista è ancora in preparazione.</div>
        ) : (
          <>
            {toGift.length === 0 && (
              <div className="text-center text-[#4E7363] py-8" data-testid="gift-all-done">Tutti i capi sono già stati regalati. Grazie di cuore! 🤍</div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {toGift.map((it) => <GiftCard key={it.product_id} it={it} accent={accent} buying={buying}
                onBuy={() => setBuyTarget(it)} onMark={() => setMarkItem(it)} />)}
            </div>
            {gifted.length > 0 && (
              <div className="mt-14" data-testid="gift-already-section">
                <h2 className="font-serif text-2xl text-[#2D2825] mb-1">Già regalati</h2>
                <p className="text-sm text-[#8C7F78] mb-5">Questi capi sono già stati scelti da qualcuno: nessun doppione!</p>
                <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
                  {gifted.map((it) => (
                    <div key={it.product_id} data-testid={`gift-done-${it.product_id}`}
                      className="rounded-xl bg-white border border-[#EAE3DC] overflow-hidden opacity-70">
                      {it.image && <img src={it.image} alt={it.title} className="w-full aspect-square object-cover grayscale-[40%]" />}
                      <div className="p-2">
                        <div className="text-[11px] text-[#2D2825] line-clamp-2">{it.title}</div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#4E7363]" data-testid={`gift-status-${it.product_id}`}><CheckCircle2 size={11} /> Già regalato</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <GiftNameDialog open={!!markItem} onOpenChange={(o) => !o && setMarkItem(null)} mark accent={accent}
          title="L'hai già regalato?" cta="Sì, l'ho regalato io" onConfirm={confirmMark}
          description={`Lo segniamo come regalato così nessuno lo comprerà due volte. ${markItem?.title || ""}`} />
        <GiftNameDialog open={!!buyTarget} onOpenChange={(o) => !o && setBuyTarget(null)} accent={accent}
          title="Da parte di chi?" cta="Vai al pagamento con il 15%" onConfirm={regala}
          description={buyTarget?.all ? "Regali tutti i capi che mancano. Scrivi il tuo nome: la mamma saprà che il regalo è tuo." : `Regali: ${buyTarget?.title || ""}. Scrivi il tuo nome: la mamma saprà che il regalo è tuo.`} />

        <div className="text-center mt-12">
          <a href="https://www.minimebaby.it" target="_blank" rel="noreferrer"
            className="text-[#C87D75] font-display hover:underline">Scopri tutta la collezione su minimebaby.it</a>
        </div>
      </div>
    </div>
  );
}

const GiftCard = ({ it, accent, buying, onBuy, onMark }) => (
  <div data-testid={`gift-item-${it.product_id}`}
    className="rounded-2xl bg-white border border-[#EAE3DC] overflow-hidden flex flex-col">
    {it.image && <img src={it.image} alt={it.title} className="w-full aspect-square object-cover" />}
    <div className="p-3 sm:p-4 flex flex-col flex-1">
      <h3 className="font-display text-[13px] sm:text-sm font-medium text-[#2D2825] line-clamp-2 min-h-[36px] sm:min-h-[42px]">{it.title}</h3>
      <div className="text-[11px] sm:text-sm text-[#8C7F78] mt-1">
        {it.size ? `Taglia ${it.size} · ` : ""}{it.color ? `${it.color} · ` : ""}
        {`Quantità ${it.quantity}`}
      </div>
      <div className="mt-1.5"><GiftStatus it={it} /></div>
      <div className="font-serif text-lg sm:text-xl mt-1" style={{ color: accent }}>{it.price ? `€ ${it.price}` : ""}</div>
      <div className="mt-auto pt-3 flex flex-col gap-1.5">
        <button onClick={onBuy} disabled={!!buying} data-testid={`gift-buy-${it.product_id}`}
          className="inline-flex items-center justify-center gap-1.5 rounded-full text-white text-xs sm:text-sm py-2 sm:py-2.5 transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ background: accent }}>
          {buying === it.product_id ? <Loader2 className="animate-spin" size={14} /> : <GiftIcon size={14} />} Regala
        </button>
        <button onClick={onMark} data-testid={`gift-mark-${it.product_id}`}
          className="text-[11px] sm:text-xs text-[#8C7F78] hover:text-[#2D2825] underline-offset-2 hover:underline transition-colors">
          L'ho già regalato io
        </button>
      </div>
    </div>
  </div>
);
